import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Check } from "lucide-react";

const CIBLES = [
  { key: "particulier", label: "Je suis un particulier", helper: "Cuisine, dressing, bibliothèque, escalier, agencement complet" },
  { key: "expertise", label: "Je suis un cabinet d'expertise", helper: "Dossier de sinistre — dégât des eaux, incendie, effraction" },
  { key: "professionnel", label: "Je suis un professionnel", helper: "Restaurant, commerce — agencement ou remise en état" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialCible = ["particulier", "expertise", "professionnel"].includes(params.get("cible"))
    ? params.get("cible")
    : "particulier";

  const [cible, setCible] = useState(initialCible);
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    type_projet: "",
    compagnie: "",
    numero_sinistre: "",
    nature_sinistre: "",
    type_etablissement: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setCible(initialCible);
  }, [initialCible]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.message) {
      toast.error("Nom, email et message sont requis.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, cible });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-[900px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="contact-success">
        <p className="text-eyebrow mb-6">Message reçu</p>
        <h1 className="h-display text-5xl md:text-7xl mb-8 text-balance">
          Merci.
          <br />
          <span className="italic text-primary">Je reviens vers vous</span>.
        </h1>
        <p className="text-[16px] text-foreground/85 leading-relaxed max-w-2xl mb-3">
          Votre demande est bien arrivée. Un accusé de réception vous parvient
          par email. Pour les dossiers de sinistre, retour sous cinq jours ouvrés.
        </p>
        <p className="text-serial mt-10">— Luigi Chauveau</p>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact · demander un devis"
        description="Décrivez votre projet — particulier, cabinet d'expertise ou professionnel. Formulaire adapté à votre profil. Réponse sous 48 heures, sous 5 jours pour les sinistres."
        path="/contact"
      />
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-2">
          <p className="text-serial">04 — Contact</p>
        </div>
        <div className="md:col-span-9">
          <h1 className="h-display text-[44px] sm:text-6xl md:text-7xl text-balance">
            Décrivez votre projet,
            <br />
            <span className="italic text-primary">je reviens vers vous</span>.
          </h1>
          <p className="mt-10 max-w-2xl text-[16px] text-foreground/85 leading-relaxed">
            Le formulaire adapte ses champs selon votre profil. Pour les
            cabinets d'expertise, l'identification du dossier permet un retour
            rapide et précis.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 grid md:grid-cols-12 gap-10 md:gap-16">
        <aside className="md:col-span-4">
          <p className="text-eyebrow mb-5">Vous êtes</p>
          <div className="space-y-px bg-border" data-testid="cible-selector">
            {CIBLES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCible(c.key)}
                className={`w-full text-left p-5 transition-colors ${
                  cible === c.key ? "bg-foreground text-background" : "bg-background hover:bg-muted/60"
                }`}
                data-testid={`cible-option-${c.key}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-xl md:text-2xl leading-tight">{c.label}</p>
                    <p className={`text-[12px] mt-2 ${cible === c.key ? "text-background/70" : "text-muted-foreground"}`}>
                      {c.helper}
                    </p>
                  </div>
                  {cible === c.key && <Check size={18} className="shrink-0 mt-1" />}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-10">
            <p className="text-eyebrow mb-5">Joindre directement</p>
            <ul className="space-y-3">
              <li>
                <a className="link-underline-static text-[15px]" href="tel:+33761051061">
                  07 61 05 10 61
                </a>
              </li>
              <li>
                <a className="link-underline-static text-[15px]" href="mailto:contact@luigichauveau.fr">
                  contact@luigichauveau.fr
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <form className="md:col-span-8 space-y-7" onSubmit={onSubmit} data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-7">
            <Field label="Nom" required value={form.nom} onChange={onChange("nom")} testId="field-nom" />
            <Field label="Email" type="email" required value={form.email} onChange={onChange("email")} testId="field-email" />
          </div>

          <div className="grid md:grid-cols-2 gap-7">
            <Field label="Téléphone" value={form.telephone} onChange={onChange("telephone")} testId="field-telephone" />
            {cible === "particulier" && (
              <Field
                label="Type de projet"
                placeholder="Cuisine, dressing, bibliothèque…"
                value={form.type_projet}
                onChange={onChange("type_projet")}
                testId="field-type-projet"
              />
            )}
            {cible === "professionnel" && (
              <Field
                label="Type d'établissement"
                placeholder="Restaurant, boutique, hôtel…"
                value={form.type_etablissement}
                onChange={onChange("type_etablissement")}
                testId="field-type-etablissement"
              />
            )}
            {cible === "expertise" && (
              <Field label="Compagnie d'assurance" value={form.compagnie} onChange={onChange("compagnie")} testId="field-compagnie" />
            )}
          </div>

          {cible === "expertise" && (
            <div className="grid md:grid-cols-2 gap-7">
              <Field
                label="Numéro de dossier sinistre"
                value={form.numero_sinistre}
                onChange={onChange("numero_sinistre")}
                testId="field-numero-sinistre"
              />
              <Field
                label="Nature du sinistre"
                placeholder="Dégât des eaux, incendie, effraction…"
                value={form.nature_sinistre}
                onChange={onChange("nature_sinistre")}
                testId="field-nature-sinistre"
              />
            </div>
          )}

          <div>
            <label className="text-eyebrow block mb-3">Message *</label>
            <textarea
              className="w-full bg-transparent border border-border focus:border-primary p-4 text-[15px] leading-relaxed min-h-[180px] transition-colors"
              required
              value={form.message}
              onChange={onChange("message")}
              data-testid="field-message"
              placeholder={
                cible === "expertise"
                  ? "Décrivez brièvement le sinistre, la date d'instruction, les attentes du dossier."
                  : "Décrivez votre projet — usage, surface approximative, matières envisagées, calendrier souhaité."
              }
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
            <p className="text-[12px] text-muted-foreground max-w-md">
              Vos informations ne sont utilisées que pour vous répondre. Aucune
              utilisation marketing.
            </p>
            <button type="submit" className="btn-ink" disabled={sending} data-testid="contact-submit">
              {sending ? "Envoi…" : "Envoyer la demande"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-eyebrow block mb-3">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-border focus:border-primary py-3 text-[15px] transition-colors outline-none"
        data-testid={testId}
      />
    </div>
  );
}
